import React, { useId, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import AutoCompleteOptionContainer from './AutoCompleteOptionContainer';
import { Col, Container, Row } from 'react-bootstrap';
import './AutoComplete.css';
import InputField from '../InputField';

const AutoComplete = ({ options = [], onFilter, onSelect, placeholder, ariaLabel, disabled, ariaRequired }) => {
  const [typedValue, setTypedValue] = useState('');
  const [focus, setFocus] = useState(false);
  const [activeOption, setActiveOption] = useState(-1);
  const containerRef = useRef();
  const typeTimeoutId = useRef();
  const listboxId = useId();
  const optionsVisible = options.length > 0 && focus;

  const handleInput = (event) => {
    const newValue = (event.target.value || '');
    setTypedValue(newValue);
    setActiveOption(-1);
    if (typeTimeoutId.current) {
      clearTimeout(typeTimeoutId.current);
    }
    typeTimeoutId.current = setTimeout(() => {
      onFilter(newValue);
    }, 500);
  };

  const clearOnSelect = (optionIndex) => {
    setTypedValue('');
    setActiveOption(-1);
    onSelect(optionIndex);
  };

  const handleKeyDown = (event) => {
    if (!optionsVisible || !['ArrowDown', 'ArrowUp', 'Enter', 'Escape'].includes(event.key)) {
      return;
    }

    if (event.key === 'Escape') {
      event.preventDefault();
      setFocus(false);
      setActiveOption(-1);
      return;
    }

    if (event.key === 'Enter') {
      if (activeOption >= 0) {
        event.preventDefault();
        clearOnSelect(activeOption);
      }
      return;
    }

    event.preventDefault();
    const direction = event.key === 'ArrowDown' ? 1 : -1;
    setActiveOption(current => {
      if (current < 0) {
        return direction > 0 ? 0 : options.length - 1;
      }
      return (current + direction + options.length) % options.length;
    });
  };

  return (
    <Container ref={containerRef} className="auto-complete px-0" onBlur={
      e => setFocus(containerRef.current?.contains(e.relatedTarget))
    } onFocus={() => setFocus(true)}>
      <Row>
        <Col>
          <InputField 
            aria-label={ariaLabel}
            placeholder={placeholder} 
            type="search" value={typedValue} 
            onChange={handleInput} 
            disabled={disabled} 
            message={{}}
            hideMessage={true}
            role="combobox"
            aria-autocomplete="list"
            aria-expanded={optionsVisible}
            aria-controls={listboxId}
            aria-activedescendant={activeOption >= 0 ? `${listboxId}-option-${activeOption}` : undefined}
            onKeyDown={handleKeyDown}
            aria-required={ariaRequired}
          />
          <AutoCompleteOptionContainer
            id={listboxId}
            options={options}
            show={optionsVisible}
            activeOption={activeOption}
            onSelect={clearOnSelect}
            onActiveOptionChange={setActiveOption}
          />
        </Col>
      </Row>
    </Container>
  );
};

AutoComplete.propTypes = {
  options: PropTypes.array.isRequired,
  onFilter: PropTypes.func.isRequired,
  onSelect: PropTypes.func,
  placeholder: PropTypes.string.isRequired,
  ariaLabel: PropTypes.string.isRequired,
  disabled: PropTypes.bool,
};

export default AutoComplete;
